import type { IClickMapPoint, IClickedElement, IHeatmapAnalytics, IHeatmapScroll } from '../types';

import { create } from 'zustand';

// Types for different heatmap data
export type HeatmapDataType = 'clicks' | 'clickedElements' | 'analytics' | 'scroll';

export interface HeatmapCacheValue<T = unknown> {
  requestId?: string;
  data?: T;
}

export type HeatmapClicksCache = HeatmapCacheValue<IClickMapPoint[]>;
export type HeatmapClickedElementsCache = HeatmapCacheValue<IClickedElement[]>;
export type HeatmapAnalyticsCache = HeatmapCacheValue<IHeatmapAnalytics>;
export type HeatmapScrollsCache = HeatmapCacheValue<IHeatmapScroll>;

export interface IHeatmapDataStoreState {
  // Cache maps keyed by stringified query parameters
  clicksCache: Record<string, HeatmapClicksCache>;
  clickedElementsCache: Record<string, HeatmapClickedElementsCache>;
  analyticsCache: Record<string, HeatmapAnalyticsCache>;
  scrollDataCache: Record<string, HeatmapScrollsCache>;

  // Actions
  upsertClicksCache: (key: string, value: HeatmapClicksCache) => void;
  upsertClickedElementsCache: (key: string, value: HeatmapClickedElementsCache) => void;
  upsertAnalyticsCache: (key: string, value: HeatmapAnalyticsCache) => void;
  upsertScrollDataCache: (key: string, value: HeatmapScrollsCache) => void;

  // Generic getter for finding cache by requestId
  findCacheKeyByRequestId: (dataType: HeatmapDataType, requestId: string) => string | undefined;

  // Clear cache
  clearCache: (dataType?: HeatmapDataType) => void;
}

export const useHeatmapDataStore = create<IHeatmapDataStoreState>()((set, get) => ({
  clicksCache: {},
  clickedElementsCache: {},
  analyticsCache: {},
  scrollDataCache: {},

  upsertClicksCache: (key, value) => {
    set((state) => ({
      clicksCache: {
        ...state.clicksCache,
        [key]: {
          requestId: state.clicksCache[key]?.requestId ?? '',
          ...value,
        },
      },
    }));
  },

  upsertClickedElementsCache: (key, value) => {
    set((state) => ({
      clickedElementsCache: {
        ...state.clickedElementsCache,
        [key]: {
          requestId: state.clickedElementsCache[key]?.requestId ?? '',
          ...value,
        },
      },
    }));
  },

  upsertAnalyticsCache: (key, value) => {
    set((state) => ({
      analyticsCache: {
        ...state.analyticsCache,
        [key]: {
          requestId: state.analyticsCache[key]?.requestId ?? '',
          ...value,
        },
      },
    }));
  },

  upsertScrollDataCache: (key, value) => {
    set((state) => ({
      scrollDataCache: {
        ...state.scrollDataCache,
        [key]: {
          requestId: state.scrollDataCache[key]?.requestId ?? '',
          ...value,
        },
      },
    }));
  },

  findCacheKeyByRequestId: (dataType, requestId) => {
    const state = get();
    let cache: Record<string, HeatmapCacheValue>;

    switch (dataType) {
      case 'clicks':
        cache = state.clicksCache;
        break;
      case 'clickedElements':
        cache = state.clickedElementsCache;
        break;
      case 'analytics':
        cache = state.analyticsCache;
        break;
      case 'scroll':
        cache = state.scrollDataCache;
        break;
    }

    const entries = Object.entries(cache);
    return entries.find(([, value]) => value.requestId === requestId)?.[0];
  },

  clearCache: (dataType) => {
    set((state) => {
      if (!dataType) {
        return {
          clicksCache: {},
          clickedElementsCache: {},
          analyticsCache: {},
          scrollDataCache: {},
        };
      }

      switch (dataType) {
        case 'clicks':
          return { clicksCache: {} };
        case 'clickedElements':
          return { clickedElementsCache: {} };
        case 'analytics':
          return { analyticsCache: {} };
        case 'scroll':
          return { scrollDataCache: {} };
      }
    });
  },
}));
